import { IAdresse } from 'components/AddressTypeahead/types';

export class AddressComparer {
  // Method to decide whether two addresses are considered equal in Kjederegisteret
  // Text comparison is case-insensitive and ignores leading/trailing whitespace
  // in gateadresse, v, vei, vegen og veien should be considered equal
  // in gateadresse, g, gate, gaten, gata should be considered equal
  static compareAddresses(
    address1: { gateadresse: string; postnr: string; poststed: string },
    address2: { gateadresse: string; postnr: string; poststed: string },
  ): { equal: boolean; message: string } {
    // Normalize addresses for comparison
    const normalizedAddress1 = this.normalizeAddress(address1);
    const normalizedAddress2 = this.normalizeAddress(address2);

    if (
      normalizedAddress1.gateadresse === normalizedAddress2.gateadresse &&
      normalizedAddress1.postnr === normalizedAddress2.postnr &&
      normalizedAddress1.poststed === normalizedAddress2.poststed
    ) {
      return { equal: true, message: '' };
    }

    const differences: string[] = [];

    if (normalizedAddress1.gateadresse !== normalizedAddress2.gateadresse) {
      differences.push(
        `Gateadresse er forskjellig: "${address1.gateadresse ?? ''}" vs "${address2.gateadresse ?? ''}"`,
      );
    }
    if (normalizedAddress1.postnr !== normalizedAddress2.postnr) {
      differences.push(
        `Postnummer er forskjellig: "${address1.postnr ?? ''}" vs "${address2.postnr ?? ''}"`,
      );
    }
    if (normalizedAddress1.poststed !== normalizedAddress2.poststed) {
      differences.push(
        `Poststed er forskjellig: "${address1.poststed ?? ''}" vs "${address2.poststed ?? ''}"`,
      );
    }

    return { equal: false, message: differences.join('; ') };
  }

  // Method to check if two addresses are equals and dont care about error messages
  static equals(
    address1: { gateadresse: string; postnr: string; poststed: string },
    address2: { gateadresse: string; postnr: string; poststed: string },
  ): boolean {
    return this.compareAddresses(address1, address2).equal;
  }

  /**
   * Normalizes an address for comparison by:
   * - Converting to lowercase
   * - Trimming whitespace
   * - Normalizing multiple spaces to single spaces
   * - Normalizing Norwegian street type variants
   */
  private static normalizeAddress(address: {
    gateadresse: string;
    postnr: string;
    poststed: string;
  }) {
    return {
      gateadresse: this.normalizeGateadresse(address.gateadresse),
      postnr: this.normalizeString(address.postnr),
      poststed: this.normalizeString(address.poststed),
    };
  }

  /**
   * Normalizes a gateadresse string with Norwegian street type normalization
   */
  private static normalizeGateadresse(gateadresse: string): string {
    if (!gateadresse) return '';

    let normalized = this.normalizeString(gateadresse);

    // Normalize Norwegian street types
    normalized = this.normalizeStreetTypes(normalized);

    return normalized;
  }

  /**
   * Normalizes a general string by trimming, lowercasing, and normalizing spaces
   */
  private static normalizeString(str: string): string {
    if (!str) return '';

    return str
      .trim() // Remove leading/trailing whitespace
      .toLowerCase() // Convert to lowercase
      .replace(/\s+/g, ' '); // Replace multiple spaces with single space
  }

  /**
   * Normalizes Norwegian street type variants
   */
  private static normalizeStreetTypes(str: string): string {
    let normalized = str;

    // "vei" variants - normalize all to "vei"
    // Match words ending with these suffixes OR standalone words
    normalized = normalized.replace(/(\w+)v\b/g, '$1vei'); // "hovedv" -> "hovedvei"
    normalized = normalized.replace(/(\w+)vegen\b/g, '$1vei'); // "hovedvegen" -> "hovedvei"
    normalized = normalized.replace(/(\w+)veien\b/g, '$1vei'); // "hovedveien" -> "hovedvei"
    normalized = normalized.replace(/\bv\b/g, 'vei'); // " v " -> " vei "

    // "gate" variants - normalize all to "gate"
    normalized = normalized.replace(/(\w+)g\b/g, '$1gate'); // "storg" -> "storgate"
    normalized = normalized.replace(/(\w+)gaten\b/g, '$1gate'); // "storgaten" -> "storgate"
    normalized = normalized.replace(/(\w+)gata\b/g, '$1gate'); // "storgata" -> "storgate"
    normalized = normalized.replace(/\bg\b/g, 'gate'); // " g " -> " gate "

    return normalized;
  }
}
